import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, statusPill, gridActionButton, newRecordButtonStyle, getODataRows, resolveODataTotalCount } from './listPageStyles';
import { VendorGroupsPage } from './VendorGroupsPage';
import { VendorSubGroupsPage } from './VendorSubGroupsPage';

const API_BASE = '/api/vendors';
const emptyForm = { textId: '', name: '', city: '', erpId: '', groupId: '', subGroupId: '', region1: '', region2: '', status: '', vendorCurrency: '' };
const tabs = [
  { key: 'vendors', label: 'Tedarikçiler' },
  { key: 'groups', label: 'Tedarikçi Grubu' },
  { key: 'subGroups', label: 'Ek Grup' },
];

const tabStyles = {
  tabBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: { minHeight: 36, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #fff)', color: 'var(--text-secondary, #4b5563)', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  tabActive: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
};

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function toInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

async function readApiError(res, fallback) {
  try {
    const data = await res.json();
    return data?.message || data?.Message || data?.infoMessage || data?.InfoMessage || fallback;
  } catch {
    return fallback;
  }
}

export function VendorsPageLegacy() {
  const [activeDefinition, setActiveDefinition] = useState('vendors');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [sortField, setSortField] = useState('TextId');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const getGroupLabel = useCallback((id) => groups.find(item => item.id === id)?.description || groups.find(item => item.id === id)?.textId || '-', [groups]);
  const getSubGroupLabel = useCallback((id) => subGroups.find(item => item.id === id)?.description || subGroups.find(item => item.id === id)?.textId || '-', [subGroups]);

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/vendorgroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/vendorsubgroups/Select', { headers }).then(res => res.ok ? res.json() : []),
    ]).then(([groupData, subGroupData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        filterParams = `(contains(tolower(TextId),'${q}') or contains(tolower(Name),'${q}') or contains(tolower(City),'${q}'))`;
      }
      const url = buildODataUrl(API_BASE, { pageSize, currentPage, sortField, sortDir, filterParams });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(pageRows);
      setTotalCount(await resolveODataTotalCount({ data, rows: pageRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders() }));
    } catch (error) {
      showToast('error', error.message || 'Veri alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [filterText, sortField, sortDir, pageSize, currentPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rowToForm = (row) => ({ textId: row.textId || '', name: row.name || '', city: row.city || '', erpId: row.erpId || '', groupId: row.groupId || '', subGroupId: row.subGroupId || '', region1: row.region1 || '', region2: row.region2 || '', status: row.status ?? '', vendorCurrency: row.vendorCurrency || '' });
  const openNew = () => { setForm(emptyForm); setSelected(null); setModal('new'); };
  const openEdit = (row = selected) => { if (!row) return; setSelected(row); setForm(rowToForm(row)); setModal('edit'); };
  const openView = (row = selected) => { if (!row) return; setSelected(row); setForm(rowToForm(row)); setModal('view'); };

  const handleSave = async () => {
    if (!form.textId.trim() || !form.name.trim()) {
      showToast('error', 'Tedarikçi kodu ve adı zorunludur');
      return;
    }
    const payload = { ...form, groupId: form.groupId || null, subGroupId: form.subGroupId || null, status: toInt(form.status), customerRelationId: null, vendorCurrencyTypeDeleted20251118071733: null };
    try {
      const isNew = modal === 'new';
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(isNew ? payload : { id: selected.id, ...payload }) });
      if (!res.ok) throw new Error(await readApiError(res, isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      showToast('success', isNew ? 'Tedarikçi başarıyla eklendi' : 'Tedarikçi başarıyla güncellendi');
      setModal(null);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Tedarikçi başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const renderTabs = () => (
    <div style={tabStyles.tabBar}>
      {tabs.map(tab => <button key={tab.key} type="button" style={{ ...tabStyles.tab, ...(activeDefinition === tab.key ? tabStyles.tabActive : {}) }} onClick={() => setActiveDefinition(tab.key)}>{tab.label}</button>)}
    </div>
  );

  if (activeDefinition === 'groups') return <div style={s.container}>{renderTabs()}<VendorGroupsPage /></div>;
  if (activeDefinition === 'subGroups') return <div style={s.container}>{renderTabs()}<VendorSubGroupsPage /></div>;

  const columns = [
    { field: 'textId', headerName: 'Tedarikçi Kodu', flex: 1, minWidth: 150 },
    { field: 'name', headerName: 'Adı', flex: 1.4, minWidth: 220 },
    { field: 'city', headerName: 'Şehir', flex: 1, minWidth: 120, renderCell: (params) => params.row.city || '-' },
    { field: 'groupLabel', headerName: 'Tedarikçi Grubu', flex: 1.2, minWidth: 170, sortable: false, renderCell: (params) => getGroupLabel(params.row.groupId) },
    { field: 'subGroupLabel', headerName: 'Ek Grup', flex: 1.1, minWidth: 150, sortable: false, renderCell: (params) => getSubGroupLabel(params.row.subGroupId) },
    { field: 'status', headerName: 'Durum', width: 130, renderCell: (params) => statusPill(params.row.status) },
    { field: 'actions', headerName: 'İşlemler', width: 250, sortable: false, filterable: false, renderCell: (params) => <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}><button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openView(params.row); }}>Görüntüle</button><button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Değiştir</button><button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }} onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}>Sil</button></div> },
  ];
  const isView = modal === 'view';
  const inputStyle = isView ? s.inputView : s.input;
  const selectStyle = isView ? s.selectView : s.select;
  const inputField = (label, key) => <div style={s.formGroup}><label style={s.label}>{label}</label><input style={inputStyle} value={form[key]} onChange={e => setForm(current => ({ ...current, [key]: e.target.value }))} readOnly={isView} /></div>;

  return (
    <div style={s.container}>
      {renderTabs()}
      <div style={s.pageHeader}><div style={s.pageHeaderText}><h2 style={s.pageTitle}>Tedarikçi Tanım Yönetimi</h2><p style={s.pageSubtitle}>Tedarikçi kayıtlarını filtreleyin, sıralayın ve yönetin.</p></div><button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Tedarikçi</button></div>
      <div style={s.toolbar}><div style={s.toolbarLeft}><input style={s.filterInput} type="text" placeholder="Kod, ad veya şehir ile filtrele..." value={filterInput} onChange={e => setFilterInput(e.target.value)} onKeyDown={event => { if (event.key === 'Enter') { setFilterText(filterInput); setCurrentPage(1); } }} />{filterText && <button style={s.btnSecondary} onClick={() => { setFilterText(''); setFilterInput(''); setCurrentPage(1); }}>Temizle</button>}<button style={s.btnSecondary} onClick={fetchData}>Yenile</button></div></div>
      <StandardDataGrid rows={rows} columns={columns} totalCount={totalCount} loading={loading} pageSize={pageSize} currentPage={currentPage} onPageChange={page => setCurrentPage(page)} onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }} sortField={sortField} sortDir={sortDir} onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'TextId'); setSortDir(direction || 'asc'); }} selectedId={selected?.id} onRowClick={row => setSelected(row)} onRowDoubleClick={row => openView(row)} exportFileName="tedarikciler" />

      {modal && <div style={s.overlay} onClick={() => setModal(null)}><div style={{ ...s.modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}><div style={s.modalHeader}><h3 style={s.modalTitle}>{modal === 'new' ? 'Yeni Tedarikçi' : modal === 'edit' ? 'Tedarikçi Düzenle' : 'Tedarikçi Detay'}</h3><button style={s.closeBtn} onClick={() => setModal(null)}>x</button></div><div style={s.modalBody}><div style={s.formRow}>{inputField('Tedarikçi Kodu *', 'textId')}{inputField('Adı *', 'name')}</div><div style={s.formRow}>{inputField('Şehir', 'city')}{inputField('ERP Kodu', 'erpId')}</div><div style={s.formRow}><div style={s.formGroup}><label style={s.label}>Tedarikçi Grubu</label>{isView ? <input style={s.inputView} readOnly value={getGroupLabel(form.groupId)} /> : <select style={selectStyle} value={form.groupId} onChange={e => setForm(current => ({ ...current, groupId: e.target.value }))}><option value="">Seçiniz</option>{groups.map(item => <option key={item.id} value={item.id}>{item.textId}{item.description ? ` - ${item.description}` : ''}</option>)}</select>}</div><div style={s.formGroup}><label style={s.label}>Ek Grup</label>{isView ? <input style={s.inputView} readOnly value={getSubGroupLabel(form.subGroupId)} /> : <select style={selectStyle} value={form.subGroupId} onChange={e => setForm(current => ({ ...current, subGroupId: e.target.value }))}><option value="">Seçiniz</option>{subGroups.map(item => <option key={item.id} value={item.id}>{item.textId}{item.description ? ` - ${item.description}` : ''}</option>)}</select>}</div></div><div style={s.formRow}>{inputField('Bölge 1', 'region1')}{inputField('Bölge 2', 'region2')}</div><div style={s.formRow}>{inputField('Durum', 'status')}{inputField('Para Birimi', 'vendorCurrency')}</div></div><div style={s.modalFooter}>{!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}<button style={s.btnSecondary} onClick={() => setModal(null)}>{isView ? 'Kapat' : 'İptal'}</button></div></div></div>}
      {deleteConfirm && <div style={s.overlay} onClick={() => setDeleteConfirm(false)}><div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}><div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div><div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.name}"</strong> tedarikçisini silmek istediğinize emin misiniz?</p></div><div style={s.modalFooter}><button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button><button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button></div></div></div>}
      <Toast toast={toast} />
    </div>
  );
}
